import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, CheckCircle2, Package, FileText, Award } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const Pistachios = () => {
  const inShellProducts = [
    {
      id: 'roasted-salted',
      name: 'Roasted & Salted In-Shell',
      bestFor: 'Retail snack packs, vending, convenience stores',
      gradeMeaning: 'Naturally opened, roasted with salt',
      qualityFocus: 'High open-shell ratio, uniform roasting',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/ab2dc43240e588bb1d2657600f16cf08a6f730265817575a805f72b5a4465aa0.png'
    },
    {
      id: 'raw-inshell',
      name: 'Raw In-Shell Pistachios',
      bestFor: 'Further processing, custom roasting programs',
      gradeMeaning: 'Unprocessed naturally opened pistachios',
      qualityFocus: 'Clean shells, good kernel fill',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/ab2dc43240e588bb1d2657600f16cf08a6f730265817575a805f72b5a4465aa0.png'
    }
  ];

  const kernelProducts = [
    {
      id: 'green-whole',
      name: 'Green Whole Kernels',
      bestFor: 'Premium confectionery, ice cream, bakery',
      gradeMeaning: 'Bright green peeled kernels; highest grade',
      qualityFocus: 'Vibrant green color, uniform size'
    },
    {
      id: 'green-split',
      name: 'Green Split Kernels',
      bestFor: 'Bakery toppings, confectionery inclusions',
      gradeMeaning: 'Split green kernels; good visual appeal',
      qualityFocus: 'Clean splits, strong green color'
    },
    {
      id: 'raw-kernels',
      name: 'Raw Pistachio Kernels',
      bestFor: 'Ingredient manufacturing, further processing',
      gradeMeaning: 'Unroasted kernels for processing',
      qualityFocus: 'Good kernel integrity, low defects'
    },
    {
      id: 'roasted-kernels',
      name: 'Roasted Kernels',
      bestFor: 'Snack mixes, retail packs, HoReCa',
      gradeMeaning: 'Roasted and ready-to-eat kernels',
      qualityFocus: 'Consistent roast, good crunch'
    }
  ];

  const grades = [
    {
      name: 'Standard Export',
      description: 'Best for bulk sourcing and value-led programs',
      features: ['Standard grading + batch COA', 'Aligned to buyer/destination specifications', 'Cost-effective bulk supply']
    },
    {
      name: 'Premium (EU/USA-Ready)',
      description: 'Best for private label and strict QC retail programs',
      features: ['Tighter QC + enhanced traceability', 'Premium packaging options', 'Expanded COA panel as required']
    }
  ];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/products/dry-fruits" className="inline-flex items-center text-[#263776] hover:text-[#F47D20] mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dry Fruits
            </Link>
            <div className="text-center">
              <h1 className="text-5xl font-bold mb-4">Pistachios</h1>
              <p className="text-xl text-slate-700 mb-6">
                Premium in-shell and kernel pistachios from Iran, USA, and Turkey for snacking, bakery, and confectionery
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <span className="bg-[#263776] text-white px-4 py-2 rounded-full">In-Shell & Kernels</span>
                <span className="bg-[#F47D20] text-white px-4 py-2 rounded-full">Iran · USA · Turkey</span>
                <span className="bg-slate-700 text-white px-4 py-2 rounded-full">Batch COA Always Supplied</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Grading Options */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">Grading Options</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {grades.map((grade, index) => (
                <Card key={index} className="p-6 border-t-4 border-t-[#263776]">
                  <h3 className="text-xl font-bold text-[#263776] mb-2">{grade.name}</h3>
                  <p className="text-slate-600 mb-4">{grade.description}</p>
                  <ul className="space-y-2">
                    {grade.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* In-Shell Products */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">In-Shell Pistachios</h2>
              <p className="text-lg text-slate-600">Naturally opened pistachios for snacking and retail</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {inShellProducts.map((product) => (
                <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-56 object-cover"
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#263776] mb-2">{product.name}</h3>
                    <p className="text-sm text-[#F47D20] font-medium mb-3">{product.bestFor}</p>
                    
                    <Accordion type="single" collapsible className="mb-4">
                      <AccordionItem value="details" className="border-none">
                        <AccordionTrigger className="text-sm py-2">View Details</AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-2 text-sm">
                            <p><strong>Grade:</strong> {product.gradeMeaning}</p>
                            <p><strong>Quality Focus:</strong> {product.qualityFocus}</p>
                            <p><strong>Packaging:</strong> Bulk cartons | Retail packs available</p>
                            <p><strong>Documents:</strong> COA | Spec sheet | Lot-coded packing list</p>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                    
                    <Button asChild size="sm" className="w-full bg-[#263776] hover:bg-[#1e2a5e]">
                      <Link to={`/contact?product=pistachios-${product.id}`}>Request Quote</Link>
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Kernel Products */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Pistachio Kernels</h2>
              <p className="text-lg text-slate-600">Shelled kernels for confectionery, bakery, and manufacturing</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {kernelProducts.map((product) => (
                <Card key={product.id} className="p-6 hover:shadow-lg transition-shadow border-l-4 border-l-[#F47D20]">
                  <h3 className="text-lg font-bold text-[#263776] mb-2">{product.name}</h3>
                  <p className="text-sm text-[#F47D20] font-medium mb-2">{product.bestFor}</p>
                  <p className="text-sm text-slate-600 mb-4">{product.gradeMeaning}</p>
                  
                  <Button asChild size="sm" variant="outline" className="w-full border-[#263776] text-[#263776] hover:bg-[#263776] hover:text-white">
                    <Link to={`/contact?product=pistachios-${product.id}`}>Request Quote</Link>
                  </Button>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quality Baseline */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-[#263776] mb-6 text-center">SPANXPORT Quality Baseline</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#263776] rounded-full flex items-center justify-center mx-auto mb-3">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2">Moisture Control</h3>
                  <p className="text-sm text-slate-600">Typically 4-6% for optimal quality</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#F47D20] rounded-full flex items-center justify-center mx-auto mb-3">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2">Size Grading</h3>
                  <p className="text-sm text-slate-600">Multiple sizes: 18/20, 20/22, 22/24, 24/26</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2">Testing</h3>
                  <p className="text-sm text-slate-600">Aflatoxin and microbiological testing</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#263776] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Request a Quote for Pistachios</h2>
          <p className="text-xl mb-4 max-w-2xl mx-auto">
            Share your requirements and we'll respond with offer + lead time + documentation list
          </p>
          <div className="flex gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#F47D20] text-white hover:bg-[#d66b1a]">
              <Link to="/contact">Request Detailed Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white bg-white/10 hover:bg-white/20">
              <Link to="/products/dry-fruits">Back to Dry Fruits</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pistachios;
